import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import type { ChatSession } from '@innermate/shared';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { MessageSquare, Search, Star } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * ⌘K / Ctrl+K command-palette 스타일 채팅 검색.
 * - 세션 제목 기준 부분 문자열 매칭 (대소문자 무시)
 * - ↑↓ 로 항목 이동, Enter 선택, Esc 닫기
 * - 검색어 없을 땐 최근 세션 상위 30개 노출
 */
export function ChatSearchDialog({ open, onOpenChange }: Props) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);

  const sessionsQuery = useQuery({
    queryKey: ['chat', 'sessions'],
    queryFn: api.chatSessionList,
    enabled: open,
    staleTime: 5_000,
  });

  const sessions = sessionsQuery.data?.sessions ?? [];

  const filtered = useMemo<ChatSession[]>(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sessions.slice(0, 30);
    return sessions.filter((s) => (s.title ?? '').toLowerCase().includes(q)).slice(0, 50);
  }, [query, sessions]);

  // 열릴 때 초기화 + 포커스.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setActiveIdx(0);
    // 다이얼로그가 DOM 에 붙은 다음 포커스 (애니메이션 중에도 안정적).
    const id = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [open]);

  // 키보드 이동 시 활성 아이템을 시야에 유지.
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const el = list.querySelector<HTMLElement>(`[data-idx="${activeIdx}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIdx]);

  if (!open) return null;

  const select = (id: string) => {
    navigate({ to: `/chat/${id}` });
    onOpenChange(false);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onOpenChange(false);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => (filtered.length === 0 ? 0 : Math.min(filtered.length - 1, i + 1)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const s = filtered[activeIdx];
      if (s) select(s.id);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-start justify-center pt-[15vh] px-4 bg-black/50 backdrop-blur-sm"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onOpenChange(false);
      }}
    >
      {/* biome-ignore lint/a11y/useSemanticElements: 커스텀 모달 유지 (AttachFilesDialog 와 동일 패턴) */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="채팅 검색"
        className="w-full max-w-xl flex flex-col rounded-2xl border border-border bg-surface shadow-[var(--shadow-card)] overflow-hidden"
        onKeyDown={onKeyDown}
      >
        <div className="flex items-center gap-3 px-4 h-14 border-b border-border">
          <Search className="size-4 text-foreground-muted shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActiveIdx(0);
            }}
            placeholder="채팅 제목으로 검색…"
            className="flex-1 bg-transparent outline-none text-sm placeholder:text-foreground-muted"
            aria-label="채팅 검색"
          />
          <kbd className="hidden sm:inline-block shrink-0 text-[10px] font-medium font-sans px-1.5 py-0.5 rounded-sm border border-border bg-surface-muted text-foreground-muted">
            Esc
          </kbd>
        </div>

        <div ref={listRef} className="max-h-[50vh] overflow-y-auto py-1.5">
          {sessionsQuery.isLoading ? (
            <div className="px-4 py-8 text-sm text-foreground-muted text-center">불러오는 중…</div>
          ) : filtered.length === 0 ? (
            <div className="px-4 py-8 text-sm text-foreground-muted text-center">
              {query.trim() ? '일치하는 채팅이 없습니다' : '채팅 이력이 없습니다'}
            </div>
          ) : (
            <ul>
              {filtered.map((s, i) => {
                const active = i === activeIdx;
                return (
                  <li key={s.id}>
                    <button
                      type="button"
                      data-idx={i}
                      onClick={() => select(s.id)}
                      onMouseMove={() => setActiveIdx(i)}
                      className={cn(
                        'w-full flex items-center gap-3 px-4 h-10 text-sm text-left transition-colors',
                        active
                          ? 'bg-surface-muted text-foreground'
                          : 'text-foreground-muted hover:text-foreground',
                      )}
                    >
                      {s.isFavorite ? (
                        <Star className="size-4 shrink-0 text-accent fill-accent" />
                      ) : (
                        <MessageSquare className="size-4 shrink-0" />
                      )}
                      <span className="flex-1 truncate">{s.title || '새 채팅'}</span>
                      <span className="shrink-0 text-[11px] text-foreground-faint">
                        {formatRelative(s.updatedAt)}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="flex items-center justify-between px-4 h-10 border-t border-border text-[11px] text-foreground-muted">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <Kbd>↑</Kbd>
              <Kbd>↓</Kbd>
              이동
            </span>
            <span className="flex items-center gap-1">
              <Kbd>↵</Kbd>
              선택
            </span>
          </div>
          {filtered.length > 0 ? <span>{filtered.length}개</span> : null}
        </div>
      </div>
    </div>
  );
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="inline-block text-[10px] font-sans px-1.5 py-0.5 rounded-sm border border-border bg-surface-muted">
      {children}
    </kbd>
  );
}

function formatRelative(iso: string): string {
  const d = new Date(iso);
  const now = Date.now();
  const diffMs = now - d.getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return '방금';
  if (mins < 60) return `${mins}분 전`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}시간 전`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}일 전`;
  return `${d.getMonth() + 1}/${d.getDate()}`;
}
